import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './SmsAlertsLaunchAnnouncementModal.css';

const DEMO_VIDEO_URL =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/video/product-sms2.mp4';

interface SmsAlertsLaunchAnnouncementModalProps {
  isOpen: boolean;
  /** "Set up SMS Alerts" CTA — closes the modal and chains into bulk-manage. */
  onPrimary: () => void;
  /** "Maybe later", backdrop click, Escape. */
  onDismiss: () => void;
}

/**
 * One-time post-login educational modal announcing the SMS Alerts feature.
 * Distinct from `SmsAlertsIntroModal` (the consent gate that fires when the
 * user takes an enabling action). This one fires proactively and is purely
 * informational — primary CTA drops the user into the bulk-manage flow.
 */
export const SmsAlertsLaunchAnnouncementModal: React.FC<
  SmsAlertsLaunchAnnouncementModalProps
> = ({ isOpen, onPrimary, onDismiss }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onDismiss]);

  // Respect prefers-reduced-motion: pause the video on first frame so the
  // hero still shows imagery, but no autoplay animation.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!isOpen) return null;

  return createPortal(
    <div
      className="sms-launch-announcement-overlay"
      onClick={onDismiss}
      role="presentation"
    >
      <div
        className="sms-launch-announcement-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sms-launch-announcement-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sms-launch-announcement-modal__video-wrap">
          <video
            className="sms-launch-announcement-modal__video"
            autoPlay={!prefersReducedMotion}
            loop
            muted
            playsInline
            preload="auto"
          >
            <source src={DEMO_VIDEO_URL} type="video/mp4" />
          </video>
        </div>

        <div className="sms-launch-announcement-modal__body">
          <h2
            id="sms-launch-announcement-title"
            className="sms-launch-announcement-modal__title"
          >
            SMS Alerts have arrived
          </h2>
          <p className="sms-launch-announcement-modal__text">
            Get a text the moment someone wants a card you have.
          </p>
          <button
            type="button"
            className="sms-launch-announcement-modal__cta"
            onClick={onPrimary}
          >
            Set up SMS Alerts
          </button>
          <button
            type="button"
            className="sms-launch-announcement-modal__dismiss"
            onClick={onDismiss}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SmsAlertsLaunchAnnouncementModal;
